// A HARANG + az értesítés-panel KÖZÖS váza — MINDKÉT felületen (belső topbar és partner-portál).
//
// MIÉRT KÖZÖS (user-lelet): „egyáltalán nem úgy néz ki a harang alatti legördülő, mint az admin
// oldalon". Valóban: a portálon egy külön, LESZŰKÍTETT másolat élt — szűkebb panel (320 vs 364 px),
// nagybetűs `overline` fejléc, nyers `<button>` a „Mind olvasott" helyén, és HIÁNYZOTT a csontváz, a
// hiba-állapot, az EmptyState, a régebbi értesítések végtelen görgetése és a lista-footer. Ez a váz a
// FELÉPÍTÉST tartja egy helyen; ami a két felületen VALÓBAN más, az propként jön:
//
//  • a mélylink-feloldás és a navigáció (két különböző, TIPIZÁLT route-fa),
//  • a szövegek (a portál ÖNÖZ, a belső felület tegez — lásd `foundations/design.md` §8.4),
//  • az ÉLŐ csatorna bekötése (`useNotificationStream`) — MINDKÉT felület használja, de a hook a
//    bekötő komponensben hívódik, hogy a váz maga renderelés-tiszta maradjon.
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useState, type ReactNode } from 'react';

import { type NotificationRow } from '../api';
import { useNotificationFeed } from '../hooks/use-notification-feed';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '../hooks/use-notification-mutations';
import { badgeLabel } from '../render';
import { NotificationItem } from './notification-item';

import { EmptyState } from '@/components/shared/empty-state';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';

/** A panel szövegei — a két felület hangneme külön kulcs-készletből jön (önözés vs tegezés). */
export interface NotificationBellTexts {
  /** A harang-gomb `title`-je és `aria-label`-je (szám nélkül / számmal). */
  triggerTitle: string;
  triggerAria: string;
  triggerAriaWithCount: string;
  panelTitle: string;
  markAll: string;
  emptyTitle: string;
  emptyDescription: string;
  errorTitle: string;
  errorDescription: string;
  loading: string;
  /** Képernyőolvasónak: az olvasatlan-szám VÁLTOZÁSA (`aria-live`) — a jelvény csak vizuális. */
  liveCount: string;
  /** A footer-link felirata; `onOpenList` nélkül nincs footer. */
  openList?: string;
}

export interface NotificationBellBaseProps<TRoute> {
  texts: NotificationBellTexts;
  /** A logikai `resourceRef` → felület-specifikus route (vagy `null`, ha nincs hova mélylinkelni). */
  resolveRoute: (resourceRef: string | null, type: string) => TRoute | null;
  /**
   * Navigálás a feloldott route-ra (a tipizált router miatt a hívó dolga). A TELJES SORT is megkapja, mert a
   * partner-portálon a navigálás KONTEXTUST is vált: az értesítés `data.accountId`-ja alapján átállítja a
   * fiók-scope-ot, különben a mélylink olyan jegyre visz, ami a szűkített listán nem is szerepel.
   */
  navigateToRoute: (route: TRoute, row: NotificationRow) => void;
  /** A footer navigációja (teljes üzenet-/értesítés-lista); nélküle a footer kimarad. */
  onOpenList?: () => void;
  /**
   * Opcionális JELZÉS a panel fejlécében: a feed NEM szűkül a felület aktuális scope-jára. A partner-portálon
   * ez akkor jelenik meg, ha a fiók-váltó szűkít — az értesítés ugyanis SZEMÉLYRE szól, nem fiókra (egy
   * elrejtett értesítés elveszett értesítés), a lapok viszont szűkülnek. A jelzés nélkül a felhasználó a
   * szűkítést mindenre érvényesnek hiszi.
   */
  scopeNote?: ReactNode;
}

export function NotificationBellBase<TRoute>({
  texts,
  resolveRoute,
  navigateToRoute,
  onOpenList,
  scopeNote,
}: NotificationBellBaseProps<TRoute>): JSX.Element {
  const [open, setOpen] = useState(false);

  const feed = useNotificationFeed();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const { unreadCount } = feed;

  const activateRow = (row: NotificationRow): void => {
    const route = resolveRoute(row.resourceRef, row.type);
    if (row.readAt === null) markRead.mutate({ id: row.id });
    // A panel CSAK navigáláskor záródik. Mélylink nélküli sornál (törölt meeting) a kattintás csak
    // olvasottnak jelöl → maradjon nyitva, hogy több sort is le lehessen zárni egymás után.
    if (route !== null) {
      setOpen(false);
      navigateToRoute(route, row);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative size-9 [&_svg]:size-[18px]"
            title={texts.triggerTitle}
            // Az interpolációs változó SZÁNDÉKOSAN `unread` (nem `count`): a `count` az i18next plural-kulcsa
            // lenne (`_one`/`_other` variánsokat követelne) — itt egy sima szám-behelyezés kell.
            aria-label={unreadCount > 0 ? texts.triggerAriaWithCount : texts.triggerAria}
          >
            <BellIcon />
            {unreadCount > 0 && (
              // Számláló-plakett: a LÁTHATÓ ikon-doboz jobb-felső sarkára ül (a 36px-es gomb-doboz
              // koordinátáiban), nem az ikon rajzolt középpontjára. Létező tokenek (danger + white).
              <span
                aria-hidden="true"
                className="bg-danger-500 absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white"
              >
                {badgeLabel(unreadCount)}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[364px] max-w-[92vw] p-0">
          {/* A panel FEJLÉCE alatt SZÁNDÉKOSAN nincs vonal (user-döntés, 2026-08-26): modál/felugró fejléc
              alatt a hierarchiát a tipográfia és a térköz adja — ugyanaz a szabály, mint a `DialogHeader`-nél. */}
          <div className="flex items-center justify-between gap-2 px-3 py-2">
            <div className="flex min-w-0 items-center gap-2">
              <Text variant="itemTitle" as="span">
                {texts.panelTitle}
              </Text>
              {scopeNote}
            </div>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
              >
                <CheckIcon />
                {texts.markAll}
              </Button>
            )}
          </div>

          {/* A RÉGEBBI értesítések VÉGTELEN GÖRGETÉSSEL töltődnek (a panel aljára érve) — a korábbi
              „Továbbiak" GOMB helyett, ami a panelen belül lapozott, és ezért félreérthető volt: a
              felhasználó a lista-oldalra navigálást várt tőle (user-lelet, 2026-08-31). A footer mostantól
              egyetlen, egyértelmű LINK az üzenet-listára. */}
          <div
            className="scrollbar-hover max-h-[min(60vh,420px)] overflow-y-auto p-1"
            onScroll={(e) => {
              const el = e.currentTarget;
              if (!feed.hasMore || feed.isFetchingMore) return;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) feed.loadMore();
            }}
          >
            {feed.isLoading ? (
              <div className="space-y-2 p-2" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Skeleton className="size-7 shrink-0 rounded-lg" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3 w-1/2" />
                      <Skeleton className="h-3 w-4/5" />
                    </div>
                  </div>
                ))}
              </div>
            ) : feed.isError ? (
              <EmptyState
                icon={BellIcon}
                title={texts.errorTitle}
                description={texts.errorDescription}
                className="border-0 p-6"
              />
            ) : feed.rows.length === 0 ? (
              <EmptyState
                icon={BellIcon}
                title={texts.emptyTitle}
                description={texts.emptyDescription}
                className="border-0 p-6"
              />
            ) : (
              <div className="flex flex-col">
                {feed.rows.map((row) => (
                  <NotificationItem
                    key={row.id}
                    row={row}
                    navigable={resolveRoute(row.resourceRef, row.type) !== null}
                    onActivate={() => activateRow(row)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* A tovább-töltés jelzése a lista ALJÁN (a görgetés indítja) — nem gomb, csak állapot. */}
          {feed.isFetchingMore && (
            <Text variant="secondary" as="p" className="px-3 pb-2 text-center">
              {texts.loading}
            </Text>
          )}

          {/* FOOTER: EGYETLEN, navigáló link a lista-oldalra (a panel a friss értesítéseket adja, a teljes
              történet a modul lapján él). A vonal FÖLÖTTE marad, de BEHÚZOTT (közös `Divider`). */}
          {onOpenList !== undefined && texts.openList !== undefined && (
            <>
              <Divider spacing="none" />
              <div className="p-1">
                {/* GOMB (nem `Link`): a panel minden navigációja a hívó `navigate`-jén megy — a sorok is így
                    ugranak a mélylinkre —, és a popover ugyanabban a lépésben zárul. */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setOpen(false);
                    onOpenList();
                  }}
                >
                  {texts.openList}
                  <ArrowRightIcon className="size-4" aria-hidden />
                </Button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>
      {/* Az olvasatlan-szám VÁLTOZÁSÁT képernyőolvasónak is jelezzük (a badge csak vizuális, `aria-hidden`). */}
      <span className="sr-only" aria-live="polite">
        {texts.liveCount}
      </span>
    </>
  );
}
